import React, { useCallback, useMemo } from 'react'
import { Link, useNavigate } from 'react-router'
import { AvatarWithoutDot } from '@components/Avatar/Avatar'
import { AvatarAtoms } from '@components/Avatar/Avatar.css'
import {
    isImageAttachment,
    isImageUrlAttachment,
    isRegularFileAttachment,
    isTickerAttachment,
    isUnfurledLinkAttachment,
} from '@components/MessageAttachments/MessageAttachments'
import { EmbeddedAttachmentsContext } from '@components/MessageAttachments/MessageAttachmentsContext'
import { useFocusMessage, useMessageLink } from '@components/MessageTimeline/hooks/useFocusItem'
import { ProfileHoverCard } from '@components/ProfileHoverCard/ProfileHoverCard'
import { RichTextPreview } from '@components/RichTextPlate/RichTextPreview'
import { MinimalTicker } from '@components/TradingChart/Ticker'
import {
    Attachment,
    EmbeddedMessageAttachment,
    isDMChannelStreamId,
    isGDMChannelStreamId,
    MessageType,
} from '@towns-protocol/sdk'
import { Box, Icon, Paragraph, Stack, Text } from '@ui'
import { useCreateLink } from 'hooks/useCreateLink'
import { PATHS } from 'routes'
import { TextProps } from 'ui/components/Text/Text'
import { shortAddress } from 'ui/utils/utils'
import { useChannelWithId, useUserLookupContext } from 'use-towns-client'
import { formatDate } from 'utils/formatDates'
import { getPrettyDisplayName } from 'utils/getPrettyDisplayName'
import { getShortTextFromMarkdown } from 'utils/getShortTextFromMarkdown'
import { isChannelMessageEventWithTextOrImageContent } from 'utils/isTimelineEventWithChannelMessageContent'

export const EmbeddedMessage = (props: {
    attachment: EmbeddedMessageAttachment
    attachmentChildren?: React.ReactNode
}) => {
    const { attachment, attachmentChildren } = props

    const attachedMessage = isChannelMessageEventWithTextOrImageContent(
        attachment.channelMessageEvent,
    )
        ? attachment.channelMessageEvent
        : undefined

    if (!attachedMessage) {
        return null
    }

    return (
        <EmbeddedAttachmentsContext.Provider value={{ isMessageAttachementContext: true }}>
            <Box gap padding background="level2" rounded="sm">
                <EmbeddedMessageHeader attachment={attachment} />
                <Box gap="md">
                    {attachedMessage.content.msgType === MessageType.Image ||
                    attachedMessage.attachments?.some(isImageUrlAttachment) ? (
                        <Box gap="sm">
                            <Box horizontal gap="xs" alignItems="center">
                                <Icon type="camera" size="square_sm" />
                                <Text color="gray1">Image</Text>
                            </Box>
                            {attachedMessage.body && (
                                <RichTextPreview
                                    content={attachedMessage.body}
                                    mentions={attachedMessage.mentions}
                                />
                            )}
                        </Box>
                    ) : attachedMessage.body ? (
                        <RichTextPreview
                            content={attachedMessage.body}
                            mentions={attachedMessage.mentions}
                        />
                    ) : null}
                    {attachmentChildren}
                </Box>

                <EmbeddedMessageFooter attachment={attachment} />
            </Box>
        </EmbeddedAttachmentsContext.Provider>
    )
}

export const EmbeddedMessageReply = (props: {
    attachment: EmbeddedMessageAttachment
    attachmentChildren?: React.ReactNode
}) => {
    const { attachment } = props
    const messageContent = useMemo(() => {
        const { channelMessageEvent } = attachment
        if (isChannelMessageEventWithTextOrImageContent(channelMessageEvent)) {
            // Check for legacy image messages
            if (channelMessageEvent.content.msgType === MessageType.Image) {
                return {
                    type: 'image' as const,
                    content: getShortTextFromMarkdown(channelMessageEvent.body || 'Image'),
                    imageUrl: channelMessageEvent.content.info?.url,
                }
            } else {
                return {
                    type: 'text' as const,
                    content: getShortTextFromMarkdown(channelMessageEvent.body),
                }
            }
        }
    }, [attachment])

    const navigate = useNavigate()
    const { refocusItem } = useFocusMessage((s) => s.actions)
    const handleClick = useCallback(() => {
        // Update URL hash for highlighting
        navigate(`#${attachment.info.messageId}`, { replace: false })
        // Also call refocusItem to trigger scroll (handles repeated clicks on same message)
        refocusItem(attachment.info.messageId, 'forced')
    }, [attachment.info.messageId, navigate, refocusItem])

    if (!messageContent && !attachment.channelMessageEvent?.attachments?.length) {
        return null
    }

    return (
        <EmbeddedAttachmentsContext.Provider value={{ isMessageAttachementContext: true }}>
            <Box horizontal maxWidth="800" width="100%">
                <Box
                    horizontal
                    hoverable
                    gap="sm"
                    paddingLeft="paragraph"
                    paddingY="sm"
                    paddingRight="md"
                    overflow="hidden"
                    rounded="md"
                    cursor="pointer"
                    background="level2"
                    alignItems="center"
                    onClick={handleClick}
                >
                    <EmbeddedMessageHeader
                        attachment={attachment}
                        fontWeight="normal"
                        fontSize="md"
                        color="gray2"
                        avatarSize="avatar_xs"
                    />
                    {messageContent?.type === 'image' ? (
                        <Box horizontal gap="xs" alignItems="center">
                            <Box
                                as="img"
                                src={messageContent.imageUrl}
                                alt={messageContent.content}
                                width="x3"
                                height="x3"
                                rounded="xs"
                                objectFit="cover"
                                insetY="xxs"
                            />
                            <Paragraph truncate color="gray2" whiteSpace="nowrap" size="sm">
                                {messageContent.content}
                            </Paragraph>
                        </Box>
                    ) : messageContent?.content ? (
                        <Paragraph truncate color="gray1" whiteSpace="nowrap">
                            {messageContent.content}
                        </Paragraph>
                    ) : null}
                    {attachment.channelMessageEvent?.attachments && (
                        <MessageAttachmentIcons
                            attachments={attachment.channelMessageEvent?.attachments}
                        />
                    )}
                </Box>
            </Box>
        </EmbeddedAttachmentsContext.Provider>
    )
}

const EmbeddedMessageHeader = (
    props: {
        attachment: EmbeddedMessageAttachment
    } & Pick<TextProps, 'fontWeight' | 'fontSize' | 'color'> & {
            avatarSize?: AvatarAtoms['size']
        },
) => {
    const {
        attachment,
        fontWeight = 'strong',
        fontSize = 'sm',
        color = 'gray1',
        avatarSize = 'avatar_xs',
    } = props

    const { lookupUser } = useUserLookupContext()
    const user = lookupUser(attachment.info.userId)
    const navigate = useNavigate()

    const { createLink } = useCreateLink()
    const userTooltip = user ? <ProfileHoverCard userId={attachment.info.userId} /> : undefined
    const onUserClick = useCallback(() => {
        if (user) {
            const link = createLink({ profileId: user.userId })
            if (link) {
                navigate(link)
            }
        }
    }, [createLink, navigate, user])

    return (
        <Stack
            horizontal
            tooltip={userTooltip}
            width="min-content"
            cursor={userTooltip ? 'pointer' : 'default'}
            gap="sm"
            alignItems="center"
            onClick={onUserClick}
        >
            <AvatarWithoutDot userId={attachment.info.userId} size={avatarSize} />
            <Paragraph fontWeight={fontWeight} color={color} size={fontSize} whiteSpace="nowrap">
                {user
                    ? `${getPrettyDisplayName(user)} `
                    : `${getPrettyDisplayName({
                          userId: attachment.info.userId,
                          username: attachment.staticInfo?.userName,
                          displayName: attachment.staticInfo?.displayName ?? '',
                      })} ${
                          attachment.staticInfo?.userName
                              ? `(${shortAddress(attachment.info.userId)})`
                              : ``
                      }`}
            </Paragraph>
        </Stack>
    )
}

const EmbeddedMessageFooter = React.memo((props: { attachment: EmbeddedMessageAttachment }) => {
    const { attachment } = props

    const { createLink } = useCreateLink()
    const channel = useChannelWithId(attachment.info.channelId)

    const channelType =
        !channel?.id || !isDMChannelStreamId(channel?.id) || !isGDMChannelStreamId(channel?.id)
            ? `Direct Message`
            : `Private Channel`

    const isKnownChannel = !!channel?.id
    const isDM =
        isDMChannelStreamId(attachment.info.channelId) ||
        isGDMChannelStreamId(attachment.info.channelId)

    const messageLinkRoot = isKnownChannel
        ? isDM
            ? createLink({ messageId: attachment.info.channelId })
            : createLink({
                  spaceId: attachment.info.spaceId,
                  channelId: attachment.info.channelId,
              })
        : undefined

    const messageLink = useMessageLink(
        `${window.location.origin}${messageLinkRoot}#${attachment.info.messageId}`,
    )

    const channelName =
        isDM || !isKnownChannel || !channel?.label ? `${channelType}` : `#${channel?.label}`

    const threadId = attachment.post?.threadId

    const items: { key: string; element: React.ReactNode }[] = []

    if (isKnownChannel && channelName) {
        items.push({
            key: 'channelName',
            element: (
                <Text size={{ desktop: 'sm', mobile: 'xs' }} whiteSpace="nowrap">
                    {threadId ? `Thread in ` : ''}
                    {channelName}
                </Text>
            ),
        })
    }
    if (attachment.info.createdAtEpochMs) {
        items.push({
            key: 'messageDate',
            element: (
                <Text size={{ desktop: 'sm', mobile: 'xs' }} whiteSpace="nowrap">
                    {formatDate(Number(attachment.info.createdAtEpochMs))}
                </Text>
            ),
        })
    }

    const navigate = useNavigate()

    if (messageLink.type === 'internal-link') {
        items.push({
            key: 'messageLink',
            element: (
                <Link to={messageLink.path} color="cta2">
                    <Text size={{ desktop: 'sm', mobile: 'xs' }} whiteSpace="nowrap" color="cta2">
                        View Message
                    </Text>
                </Link>
            ),
        })
    } else if (messageLink.type === 'same-channel-message') {
        items.push({
            key: 'messageLink',
            element: (
                <Box
                    as="a"
                    cursor="pointer"
                    onClick={() => {
                        if (threadId) {
                            const threadPath = `/${PATHS.SPACES}/${attachment.info.spaceId}/${PATHS.CHANNELS}/${attachment.info.channelId}/${PATHS.REPLIES}/${threadId}#${attachment.info.messageId}`
                            navigate(threadPath)
                        } else {
                            messageLink.focusMessage()
                        }
                    }}
                >
                    <Text size={{ desktop: 'sm', mobile: 'xs' }} whiteSpace="nowrap" color="cta2">
                        View Message
                    </Text>
                </Box>
            ),
        })
    }
    return (
        <Stack horizontal gap="sm" color="gray2" alignItems="center" fontSize="sm" flexWrap="wrap">
            {items.map((item, index) => (
                <React.Fragment key={item.key}>
                    {item.element}
                    {index < items.length - 1 && <Text>&bull;</Text>}
                </React.Fragment>
            ))}
        </Stack>
    )
})

EmbeddedMessageFooter.displayName = 'EmbeddedMessageFooter'

const MessageAttachmentIcons = (props: { attachments: Attachment[] }) => {
    const { attachments } = props

    if (attachments) {
        const imageAttachments = attachments.filter((attachment) => isImageAttachment(attachment))
        const imageUrlAttachments = attachments.filter((attachment) =>
            isImageUrlAttachment(attachment),
        )

        const fileAttachments = attachments.filter((attachment) =>
            isRegularFileAttachment(attachment),
        )
        const linkAttachments = attachments.filter((attachment) =>
            isUnfurledLinkAttachment(attachment),
        )

        const tickerAttachments = attachments.filter((attachment) => isTickerAttachment(attachment))

        const hasAttachments =
            imageAttachments.length > 0 ||
            imageUrlAttachments.length > 0 ||
            fileAttachments.length > 0 ||
            linkAttachments.length > 0 ||
            tickerAttachments.length > 0

        const totalImages = imageAttachments.length + imageUrlAttachments.length

        return hasAttachments ? (
            <Box horizontal gap="xs" color="gray2">
                {totalImages > 0 ? (
                    <Box horizontal gap="xs" alignItems="center">
                        {imageAttachments.map((a) => {
                            if (a.thumbnail?.content) {
                                const blob = new Blob([a.thumbnail.content])
                                return (
                                    <Box
                                        as="img"
                                        key={a.id}
                                        src={URL.createObjectURL(blob)}
                                        alt=""
                                        width="x2"
                                        height="x2"
                                        rounded="xs"
                                        objectFit="cover"
                                    />
                                )
                            } else {
                                return <Icon type="file" key={a.id} size="square_xs" />
                            }
                        })}
                        {imageUrlAttachments.map((a) => (
                            <Box
                                as="img"
                                key={a.id}
                                src={a.info.url}
                                alt=""
                                width="x2"
                                height="x2"
                                rounded="xs"
                                objectFit="cover"
                            />
                        ))}
                        <Text
                            size="sm"
                            whiteSpace="nowrap"
                        >{`${totalImages} Image${totalImages > 1 ? 's' : ''}`}</Text>
                    </Box>
                ) : fileAttachments.length > 0 ? (
                    <Box horizontal gap="xxs" alignItems="center">
                        <Icon type="file" size="square_xs" />
                        <Text size="sm">{`${fileAttachments.length} File${fileAttachments.length > 1 ? 's' : ''}`}</Text>
                    </Box>
                ) : linkAttachments.length > 0 ? (
                    <Box horizontal gap="xs" alignItems="center">
                        <Icon type="link" size="square_xs" />
                        <Text size="sm" whiteSpace="nowrap">
                            {`${linkAttachments.length} Link${linkAttachments.length > 1 ? 's' : ''}`}
                        </Text>
                    </Box>
                ) : tickerAttachments.length > 0 ? (
                    <Box horizontal gap="xs">
                        {tickerAttachments.map((a) => (
                            <Box key={a.id}>
                                <MinimalTicker singleLine address={a.address} chainId={a.chainId} />
                            </Box>
                        ))}
                    </Box>
                ) : null}
            </Box>
        ) : null
    }
}
